import { ID, NewTask } from '@griply/types';

import { newTaskConverter } from '../converters';
import { dateToFirestoreTimestamp } from '../converters/date-to-firestore-timestamp';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';
import { withRoles } from '../utils/with-roles';

export type CreateTasksOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TTimestamp = unknown,
  TDb = FirestoreInstance,
> = {
  tasks: NewTask[];
  userId: ID;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: ID };
  setDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<ID, unknown>,
  ) => Promise<void> | void;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  timestampFromDate: (date: Date) => TTimestamp;
  writeBatch?: (db: NoInfer<TDb>) => {
    set: (ref: NoInfer<TDocRef>, data: Record<ID, unknown>) => void;
    commit: () => Promise<void>;
  };
};

const buildTaskDocument = <TDocRef, TFieldValue, TTimestamp>(
  task: NewTask,
  docRef: TDocRef & { id: ID },
  userId: ID,
  serverTimestamp: () => TFieldValue,
  timestampFromDate: (date: Date) => TTimestamp,
): Record<string, unknown> => {
  const converted = withRoles(newTaskConverter(task), userId);
  const firestoreData = dateToFirestoreTimestamp(converted, timestampFromDate);

  return {
    ...firestoreData,
    id: docRef.id,
    updatedAt: serverTimestamp(),
    parentIds: null,
  };
};

export const createTasks = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TTimestamp,
  TDb = FirestoreInstance,
>(
  options: CreateTasksOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TTimestamp,
    TDb
  >,
): Promise<ID[]> => {
  const {
    tasks,
    userId,
    collection,
    doc,
    setDoc,
    db,
    serverTimestamp,
    timestampFromDate,
    writeBatch,
  } = options;

  const taskCollection = collection(db, FirestoreCollections.Tasks);
  const ids: ID[] = [];

  if (tasks.length === 1 || !writeBatch) {
    const promises = tasks.map((task) => {
      const docRef = doc(taskCollection);
      ids.push(docRef.id);
      const data = buildTaskDocument(
        task,
        docRef,
        userId,
        serverTimestamp,
        timestampFromDate,
      );
      return setDoc(docRef, data);
    });
    await Promise.all(promises);
    return ids;
  }

  const batch = writeBatch(db);
  tasks.forEach((task) => {
    const docRef = doc(taskCollection);
    ids.push(docRef.id);
    const data = buildTaskDocument(
      task,
      docRef,
      userId,
      serverTimestamp,
      timestampFromDate,
    );
    batch.set(docRef, data);
  });
  await batch.commit();
  return ids;
};
